<template>
  <pdl-collapse-item @state-changed="updateStateOfCollapseItem($event)">
    <template #title>
      <pdl-media-object v-if="!isCollapseActive" qaid="paint-scheme-option-summary">
        <template #media>
          <figure class="relative shadow sm:w-16 md:w-18 aspect-square" aria-hidden="true">
            <img
              qaid="paint-scheme-option-thumbnail-image"
              :src="thumbnailImage"
              alt=""
              class="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
          </figure>
        </template>
        <template #content>
          <div class="flex flex-col w-full h-full justify-center">
            <h3 :qaid="`paint-scheme-option-description-${optionQaid}`" class="text-sm font-bold">
              {{ option.description }}
            </h3>
            <p :qaid="`paint-scheme-option-current-selection-${optionQaid}`" class="text-sm text-gray-60 p-0 m-0">
              {{ selectedOption.description }}
            </p>
          </div>
        </template>
      </pdl-media-object>
      <div v-else class="flex flex-col w-full h-full justify-center">
        <h3 :qaid="`paint-scheme-option-description-${optionQaid}`" class="text-sm font-bold">
          {{ option.description }}
        </h3>
        <p :qaid="`paint-scheme-option-current-selection-${optionQaid}`" class="text-sm text-gray-60 p-0 m-0">
          {{ selectedOption.description }}
        </p>
      </div>
    </template>
    <pdl-callout
      v-if="hasMarketingCopy && isCollapseActive"
      class="mb-1"
      kind="warning"
      qaid="paint-scheme-option-current-selection-marketing-copy-callout"
    >
      <template #content>
        {{ selectedOption.marketingCopy }}
      </template>
    </pdl-callout>
    <figure aria-hidden="true" class="w-full aspect-[3] relative">
      <img
        qaid="paint-scheme-option-full-image"
        :src="fullImage"
        alt=""
        class="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
    </figure>
    <paint-scheme-swatch-groups
      :selected-option="selectedOption"
      :group-id="option.id"
      :top-tier-id="option.topTierPackageId"
      :option-category="option.description"
      :color-choices="option.choices"
    />
  </pdl-collapse-item>
</template>

<script>
import {PdlCollapseItem} from '@pedal/pdl-collapse';
import {PdlMediaObject} from '@pedal/pdl-media-object';
import {PdlCallout} from '@pedal/pdl-callout';
import PaintSchemeSwatchGroups from '@/components/containers/buy-zone/customization/PaintSchemeSwatchGroups.vue';
import {mapGetters} from 'vuex';
import {Cloudinary, CloudinaryConstants} from '@/utils/image/cloudinary-helpers';

const DELAY_FOR_COLLAPSE_EXPANSION = 325;

export default {
  name: 'PaintSchemeOption',
  components: {
    PdlCollapseItem,
    PdlMediaObject,
    PdlCallout,
    PaintSchemeSwatchGroups,
  },
  props: {
    option: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isCollapseActive: false,
    };
  },
  computed: {
    ...mapGetters('bikeCustomization', ['selections']),

    hasMarketingCopy() {
      return this.selectedOption.marketingCopy && this.selectedOption.marketingCopy.length > 0;
    },
    thumbnailImage() {
      if (!this.selectedOption?.imageThumbnailReference) return '';

      const imageTransformations = {
        w: 75,
        h: 75,
        c: 'fill',
        dpr: `${window.devicePixelRatio}`,
        ...CloudinaryConstants.DEFAULT_PARAMETERS,
      };
      return Cloudinary.createAssetUrl(this.selectedOption.imageThumbnailReference, imageTransformations);
    },
    fullImage() {
      if (!this.selectedOption?.imageThumbnailReference) return '';

      const imageTransformations = {
        w: 450,
        h: 150,
        c: 'fill',
        dpr: `${window.devicePixelRatio}`,
        ...CloudinaryConstants.DEFAULT_PARAMETERS,
      };
      return Cloudinary.createAssetUrl(this.selectedOption.imageThumbnailReference, imageTransformations);
    },
    selectedOption() {
      let result = null;
      let selected = this.selections?.find((selection) => selection.groupId === this.option.id);

      if (this.option.choices?.length) {
        if (selected) {
          result = this.option.choices.find((choice) => choice.id === selected.choiceId);
        }
        if (!result) {
          result = this.option.choices.find((choice) => choice.id === this.option.defaultChoiceId);
        }
        if (!result && this.option.choices.length) {
          result = this.option.choices[0];
        }
      }

      return result;
    },
    optionQaid() {
      return this.option?.description ? this.option.description.replace(/\s+/g, '-').toLowerCase() : '';
    },
  },
  methods: {
    updateStateOfCollapseItem(event) {
      this.isCollapseActive = event;
      setTimeout(() => {
        if (this.isCollapseActive) {
          this.scrollToActiveCollapseItem();
        }
      }, DELAY_FOR_COLLAPSE_EXPANSION);
    },
    scrollToActiveCollapseItem() {
      const collapse = document.querySelector('#paint-scheme-options-list');
      const activeCollapseItem = collapse.querySelector('.pdl-collapse-item.is-active');
      if (!activeCollapseItem) return;
      activeCollapseItem.scrollIntoView({
        behavior: !window.matchMedia(`(prefers-reduced-motion: reduce)`).matches ? 'smooth' : 'instant',
        block: 'start',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.pdl-collapse-item__wrap) {
  border-bottom: none;
  position: relative;
  z-index: 1;
  margin-right: 0.5rem;
}

:deep(.pdl-collapse-item__header) {
  &.is-sticky {
    z-index: 2;
    box-shadow: -4px 0 0 #fff, 4px 0 0 #fff;
  }
}
</style>
